import moment from "moment";
import PROCESS_TYPES from "@common/constants/processTypes";
import {
  SESSION_STATES,
  SESSION_TYPES,
} from "@common/constants/sessionConstants";
import PHASE_STATUS from "@common/constants/phaseStatus";
import { MILESTONE_STAGES } from "@common/constants/milestones";

/**
 * Fermentation stage type labels for display
 */
const FERM_STAGE_LABELS = {
  PRIM: "Primary",
  SECND: "Secondary",
  COND: "Conditioning",
};

const stageIsForceCompleted = (entryStage, currentStatus) => {
  if (!currentStatus) return false;
  const { procType, procState } = currentStatus;

  // Brewing stages: RINSING, MASHING, BOILING
  const isBrewingStage = ["RINSING", "MASHING", "BOILING"].includes(entryStage);

  // If currently fermenting, serving, or cleaning, all brewing stages are 100% completed
  if (
    isBrewingStage &&
    [
      PROCESS_TYPES.PROC_FERMENTATION,
      PROCESS_TYPES.PROC_SERVING,
      PROCESS_TYPES.PROC_CLEAN_MINIBREW,
      PROCESS_TYPES.PROC_ACID_CLEAN_MINIBREW,
    ].includes(procType)
  ) {
    return true;
  }

  // If currently serving or cleaning, fermentation is 100% completed
  if (
    entryStage === "FERMENTATION" &&
    [
      PROCESS_TYPES.PROC_SERVING,
      PROCESS_TYPES.PROC_CLEAN_MINIBREW,
      PROCESS_TYPES.PROC_ACID_CLEAN_MINIBREW,
    ].includes(procType)
  ) {
    return true;
  }

  // If currently cleaning, serving is 100% completed
  if (
    entryStage === "SERVING" &&
    [
      PROCESS_TYPES.PROC_CLEAN_MINIBREW,
      PROCESS_TYPES.PROC_ACID_CLEAN_MINIBREW,
    ].includes(procType)
  ) {
    return true;
  }

  // State-based checks within the active procType
  if (procType === PROCESS_TYPES.PROC_BREWING && procState !== null) {
    if (entryStage === "RINSING" && procState >= 20) return true;
    if (entryStage === "MASHING" && procState >= 50) return true;
    if (entryStage === "BOILING" && procState >= 74) return true;
  }

  if (procType === PROCESS_TYPES.PROC_FERMENTATION && procState !== null) {
    if (entryStage === "FERMENTATION" && procState >= 88) return true;
  }

  if (procType === PROCESS_TYPES.PROC_SERVING && procState !== null) {
    if (entryStage === "SERVING" && procState >= 101) return true;
  }

  return false;
};

/**
 * Resolves the active fermentation stage index from available status signals.
 *
 * @param {number|null} targetFermStageIdx - stage index matched from setpoint temperature
 * @param {number|null|undefined} currentStepIndex - step index from frequent log status
 * @param {number|null|undefined} currentTempStepIndex - temperature step index from frequent log status
 * @param {number|null|undefined} procState - process state
 * @returns {number} fermentation stage index (0-based)
 */
export const getFermentationStageIndex = (
  targetFermStageIdx,
  currentStepIndex,
  currentTempStepIndex,
  procState,
) => {
  if (targetFermStageIdx !== null && targetFermStageIdx !== undefined) {
    return targetFermStageIdx;
  }
  if (
    currentStepIndex !== undefined &&
    currentStepIndex !== null &&
    currentStepIndex >= 0
  ) {
    return currentStepIndex;
  }
  if (
    currentTempStepIndex !== undefined &&
    currentTempStepIndex !== null &&
    currentTempStepIndex >= 0
  ) {
    return currentTempStepIndex;
  }
  if (procState === 6) {
    return 1;
  }
  return 0;
};

/**
 * Generate a flat, ordered timeline of all brew steps with absolute timestamps.
 *
 * @param {Object} session - WorkingSession object with { created, device, ... }
 * @param {Object} recipe - Full recipe object from the API
 * @param {Object|null} currentStatus - { procState, procType } from MQTT frequent log
 * @returns {Object[]} - Array of timeline stage groups, each with entries
 */
export const generateTimeline = (session, recipe, currentStatus) => {
  if (!session || !recipe) return [];

  const sessionStart = new Date(session.brew_timestamp * 1000);

  let cumulativeMinutes = 0;
  let globalOrder = 0;
  const stages = [];
  const actuals = session.actual_step_timestamps || {};
  const milestonesList = session.milestones || [];
  const milestonesByStage = {};
  milestonesList.forEach((m) => {
    const d = new Date(m.recorded_at);
    if (!milestonesByStage[m.stage]) {
      milestonesByStage[m.stage] = [];
    }
    milestonesByStage[m.stage].push({ ...m, date: d });
  });

  // ─── 0. RINSING ───
  const rinseEntries = [];
  const rinseDuration = 2; // standard setup rinse time in recipe terms
  const rinseStart = sessionStart;
  const rinseEnd = new Date(rinseStart.getTime() + rinseDuration * 60 * 1000);

  rinseEntries.push({
    id: "rinse-0",
    stage: "RINSING",
    stageOrder: globalOrder++,
    label: "Rinse System",
    temperature: null,
    duration: rinseDuration,
    durationLabel: `${rinseDuration} min`,
    startTime: rinseStart,
    endTime: rinseEnd,
    status: "upcoming",
    isUserAction: false,
    isHopDrop: false,
    children: [],
    ingredients: [],
  });

  stages.push({
    id: "stage-rinsing",
    name: "Rinse",
    stage: "RINSING",
    entries: rinseEntries,
  });

  cumulativeMinutes += rinseDuration;

  // ─── 1. MASHING ───
  if (recipe.mashing && recipe.mashing.length > 0) {
    const mashEntries = [];
    const hasMultipleMashStages = recipe.mashing.length > 1;
    recipe.mashing.forEach((mashStage, stageIdx) => {
      if (mashStage.steps) {
        mashStage.steps.forEach((step, stepIdx) => {
          const startTime = new Date(
            sessionStart.getTime() + cumulativeMinutes * 60 * 1000,
          );
          const durationMin = step.duration || 0;
          const endTime = new Date(
            startTime.getTime() + durationMin * 60 * 1000,
          );

          const label = hasMultipleMashStages
            ? mashStage.steps.length > 1
              ? `Mash ${stageIdx + 1} - Step ${stepIdx + 1}`
              : `Mash ${stageIdx + 1}`
            : `Step ${stepIdx + 1}`;

          mashEntries.push({
            id: `mash-${stageIdx}-${stepIdx}`,
            stage: "MASHING",
            stageOrder: globalOrder++,
            label,
            temperature: step.temperature ? parseFloat(step.temperature) : null,
            duration: durationMin,
            durationLabel: `${durationMin} min`,
            startTime,
            endTime,
            status: "upcoming",
            isUserAction: false,
            isHopDrop: false,
            children: [],
            // Mash ingredient list (fermentables/cereals) — hidden for now,
            // uncomment to show grain bill on the first mash step:
            // ingredients:
            //   stageIdx === 0 && stepIdx === 0 && mashStage.ingredient_additions
            //     ? mashStage.ingredient_additions
            //         .filter((ing) => ing.ingredient_id)
            //         .map((ing) => ({
            //           name: ing.ingredient_name,
            //           amount: `${ing.amount}g`,
            //           type: ing.ingredient_type,
            //         }))
            //     : [],
            ingredients: [],
          });

          cumulativeMinutes += durationMin;
        });
      }
    });

    if (mashEntries.length > 0) {
      stages.push({
        id: "stage-mashing",
        name: "Mashing",
        stage: "MASHING",
        entries: mashEntries,
      });
    }
  }

  // ─── 2. BOILING ───
  // Firmware lifecycle after last mash step:
  //   MASH_OUT (5 min) → SPARGING (10 min) → enter BOILING state
  //   → PRE_BOIL ramp-up (5 min, heating to ~99°C) → 'boil-started'
  //   → actual boil timer starts → hop drops → 'boil-done'
  // These fixed durations match sim/src/brewer/reducer.ts constants.
  const MASH_OUT_MIN = 5;
  const SPARGING_MIN = 10;
  const PRE_BOIL_MIN = 5; // ramp from mash-out temp to boiling
  const POST_MASH_GAP = MASH_OUT_MIN + SPARGING_MIN + PRE_BOIL_MIN;

  if (recipe.boiling && recipe.boiling.length > 0) {
    const boil = recipe.boiling[0];
    const boilDuration = boil.duration || 60;

    // Actual boil start = mash end + mash-out + sparging + pre-boil ramp
    const boilStart = new Date(
      sessionStart.getTime() + (cumulativeMinutes + POST_MASH_GAP) * 60 * 1000,
    );
    const boilEnd = new Date(boilStart.getTime() + boilDuration * 60 * 1000);

    // Collect all hop/ingredient drops and sort by drop time
    const allIngredients = [
      ...(boil.hops || []).map((h) => ({ ...h, _isHop: true })),
      ...(boil.other_ingredients || []).map((i) => ({ ...i, _isHop: false })),
    ]
      .filter((ing) => ing.ingredient_id)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0)); // Higher duration = earlier drop

    const hopChildren = allIngredients.map((ing, idx) => {
      const ingDuration = ing.duration || 0;
      // duration = minutes remaining in boil when added
      // dropMinute = minute within the boil when the hop is added
      const dropMinute = boilDuration - ingDuration;
      const dropTime = new Date(boilStart.getTime() + dropMinute * 60 * 1000);

      return {
        id: `boil-hop-${idx}`,
        stage: "BOILING",
        stageOrder: globalOrder,
        label: ing.ingredient_name,
        amount: `${ing.amount}g`,
        alphaAcid: ing.alpha_acid,
        dropTime,
        dropMinute,
        durationInBoil: ingDuration,
        durationLabel: ingDuration === 0 ? "Flameout" : `@ ${dropMinute} min`,
        ingredient_id: ing.ingredient_id,
        isHop: ing._isHop,
        type: ing.ingredient_type,
        isDropped: false,
      };
    });

    const boilEntry = {
      id: "boil-0",
      stage: "BOILING",
      stageOrder: globalOrder++,
      label: "Boiling",
      temperature: 98,
      duration: boilDuration,
      durationLabel: `${boilDuration} min`,
      startTime: boilStart,
      endTime: boilEnd,
      status: "upcoming",
      isUserAction: false,
      isHopDrop: false,
      children: hopChildren,
    };

    // Calculate cooling/cleaning start offset based on the end of boiling
    const chillStart = boilEnd;
    const chillDuration = 30; // standard chill time
    const chillEnd = new Date(chillStart.getTime() + chillDuration * 60 * 1000);

    const chillEntry = {
      id: "boil-chill",
      stage: "BOILING",
      stageOrder: globalOrder++,
      label: "Cooling & Sanitizing",
      temperature: 20, // cooling targets pitching temp
      duration: chillDuration,
      durationLabel: `${chillDuration} min`,
      startTime: chillStart,
      endTime: chillEnd,
      status: "upcoming",
      isUserAction: false,
      isHopDrop: false,
      children: [],
    };

    stages.push({
      id: "stage-boiling",
      name: "Boiling",
      stage: "BOILING",
      entries: [boilEntry, chillEntry],
    });

    cumulativeMinutes += POST_MASH_GAP + boilDuration + chillDuration;
  }

  // ─── 3. FERMENTATION ───
  if (recipe.fermenting && recipe.fermenting.length > 0) {
    const fermEntries = [];

    // Parse while_fermenting additions and map them by addition_step
    const whileFermenting = recipe.while_fermenting || {};
    const fermAdditions = {};
    const allFermIngredients = [
      ...(whileFermenting.hops || []),
      ...(whileFermenting.other_ingredients || []),
    ].filter((ing) => ing.ingredient_id);

    allFermIngredients.forEach((ing) => {
      const key = ing.addition_step || "0-0";
      if (!fermAdditions[key]) fermAdditions[key] = [];
      fermAdditions[key].push(ing);
    });

    recipe.fermenting
      .sort((a, b) => a.order - b.order)
      .forEach((fermStage, stageIdx) => {
        const stageLabel =
          FERM_STAGE_LABELS[fermStage.fermentation_stage_type] ||
          fermStage.fermentation_stage_type ||
          `Stage ${stageIdx + 1}`;

        if (fermStage.steps) {
          fermStage.steps.forEach((step, stepIdx) => {
            const startTime = new Date(
              sessionStart.getTime() + cumulativeMinutes * 60 * 1000,
            );
            // API returns fermentation step durations in days. Convert to minutes for timeline calculations.
            const durationDays = step.duration || 0;
            const durationMin = durationDays * 24 * 60;
            const endTime = new Date(
              startTime.getTime() + durationMin * 60 * 1000,
            );

            // Check for yeast addition (first step of primary)
            const yeastIngredients = [];
            if (
              fermStage.fermentation_stage_type === "PRIM" &&
              stepIdx === 0 &&
              fermStage.yeast &&
              fermStage.yeast.length > 0
            ) {
              fermStage.yeast.forEach((y) => {
                if (y.ingredient_id) {
                  yeastIngredients.push({
                    name: y.ingredient_name,
                    amount: `${y.amount}g`,
                    type: "YEAST",
                    isUserAction: true,
                  });
                }
              });
            }

            // Check for while_fermenting additions at this step
            const additionKey = `${stageIdx}-${stepIdx}`;
            const stepAdditions = (fermAdditions[additionKey] || []).map(
              (ing) => ({
                name: ing.ingredient_name,
                amount: `${ing.amount}g`,
                type: ing.ingredient_type,
                duration: ing.duration,
                isUserAction: true,
              }),
            );

            const allStepIngredients = [...yeastIngredients, ...stepAdditions];

            fermEntries.push({
              id: `ferm-${stageIdx}-${stepIdx}`,
              stage: "FERMENTATION",
              stageOrder: globalOrder++,
              label:
                stageLabel +
                (fermStage.steps.length > 1 ? ` Step ${stepIdx + 1}` : ""),
              temperature: step.temperature
                ? parseFloat(step.temperature)
                : null,
              duration: durationMin,
              durationLabel:
                durationDays === 0
                  ? "—"
                  : durationDays === 1
                    ? "1 day"
                    : `${durationDays} days`,
              startTime,
              endTime,
              status: "upcoming",
              isUserAction: false,
              isHopDrop: false,
              children: allStepIngredients.map((ing, idx) => ({
                id: `ferm-ing-${stageIdx}-${stepIdx}-${idx}`,
                stage: "FERMENTATION",
                label: ing.name,
                amount: ing.amount,
                type: ing.type,
                isUserAction: ing.isUserAction,
              })),
            });

            cumulativeMinutes += durationMin;
          });
        }
      });

    if (fermEntries.length > 0) {
      stages.push({
        id: "stage-fermentation",
        name: "Fermentation",
        stage: "FERMENTATION",
        entries: fermEntries,
      });
    }
  }

  // ─── 4. SERVING ───
  const servingStart = new Date(
    sessionStart.getTime() + cumulativeMinutes * 60 * 1000,
  );
  stages.push({
    id: "stage-serving",
    name: "Serving",
    stage: "SERVING",
    entries: [
      {
        id: "serving-0",
        stage: "SERVING",
        stageOrder: globalOrder++,
        label: "Serving",
        temperature: recipe.serving_temperature
          ? parseFloat(recipe.serving_temperature)
          : null,
        duration: null,
        durationLabel: "Ongoing",
        startTime: servingStart,
        endTime: null,
        status: "upcoming",
        isUserAction: false,
        isHopDrop: false,
        children: [],
      },
    ],
  });

  // ─── 5. CLEANING ───
  stages.push({
    id: "stage-cleaning",
    name: "Cleaning",
    stage: "CLEANING",
    entries: [
      {
        id: "cleaning-0",
        stage: "CLEANING",
        stageOrder: globalOrder++,
        label: "CIP Clean",
        temperature: null,
        duration: null,
        durationLabel: "~30 min",
        startTime: null,
        endTime: null,
        status: "upcoming",
        isUserAction: true,
        isHopDrop: false,
        children: [],
      },
    ],
  });

  // ─── 4-PHASE MILESTONE PIPELINE ───
  //
  // Phase 1: Canonical structure from recipe built above.
  // Phase 2: Apply Milestones (Zone A - Completed Frozen History).
  // Phase 3: Active Step Overlay (Zone B - Live Anchored ETA, Zero Flutter).
  // Phase 4: Future Steps (Zone C - Single Start ETA Only).

  const isSessionFinished = session.status === SESSION_STATES.STATUS_FINISHED;
  const isSessionAborted =
    session.status === SESSION_STATES.STATUS_ABORTED ||
    session.status === SESSION_STATES.STATUS_FAILED;
  const isSessionActive = session.status === SESSION_STATES.STATUS_ACTIVE;

  const allEntries = stages.flatMap((s) => s.entries);

  // Helper to resolve milestone start timestamp for an entry
  const getMilestoneStart = (entry) => {
    if (entry.id === "rinse-0") {
      return (
        milestonesByStage[MILESTONE_STAGES.RINSE_START]?.[0]?.date ||
        (actuals[1] ? new Date(actuals[1]) : null)
      );
    }
    if (entry.id === "mash-0-0") {
      return (
        milestonesByStage[MILESTONE_STAGES.MASH_STEP_START]?.find(
          (m) => m.step_index === 0,
        )?.date ||
        milestonesByStage[MILESTONE_STAGES.MASH_START]?.[0]?.date ||
        (actuals[2] ? new Date(actuals[2]) : null)
      );
    }
    const mashMatch = entry.id.match(/^mash-(\d+)-(\d+)$/);
    if (mashMatch) {
      const sIdx = parseInt(mashMatch[2], 10);
      return (
        milestonesByStage[MILESTONE_STAGES.MASH_STEP_START]?.find(
          (m) => m.step_index === sIdx,
        )?.date || null
      );
    }
    if (entry.id === "boil-0") {
      return (
        milestonesByStage[MILESTONE_STAGES.BOIL_MAINTAIN_START]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.BOIL_HEATUP_START]?.[0]?.date ||
        (actuals[3] ? new Date(actuals[3]) : null)
      );
    }
    if (entry.id === "boil-chill") {
      return (
        milestonesByStage[MILESTONE_STAGES.COOLING_START]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.BOIL_END]?.[0]?.date ||
        (actuals[4] ? new Date(actuals[4]) : null)
      );
    }
    const fermMatch = entry.id.match(/^ferm-(\d+)-(\d+)$/);
    if (fermMatch) {
      const stageIdx = parseInt(fermMatch[1], 10);
      const stepIdx = parseInt(fermMatch[2], 10);
      if (stepIdx === 0) {
        if (stageIdx === 0) {
          return (
            milestonesByStage[MILESTONE_STAGES.FERMENTATION_START]?.[0]?.date ||
            milestonesByStage[MILESTONE_STAGES.PITCH_COOLING_START]?.[0]
              ?.date ||
            (actuals[5] ? new Date(actuals[5]) : null)
          );
        }
        return (
          milestonesByStage[MILESTONE_STAGES.FERMENTATION_START]?.find(
            (m) => m.step_index === stageIdx,
          )?.date ||
          (actuals[5 + stageIdx] ? new Date(actuals[5 + stageIdx]) : null)
        );
      }
      return (
        milestonesByStage[MILESTONE_STAGES.FERMENTATION_START]?.find(
          (m) => m.step_index === stageIdx,
        )?.date || null
      );
    }
    if (entry.id === "serving-0") {
      return (
        milestonesByStage[MILESTONE_STAGES.SERVING_START]?.[0]?.date ||
        (actuals[8] ? new Date(actuals[8]) : null)
      );
    }
    return null;
  };

  // Helper to resolve milestone end timestamp for an entry
  const getMilestoneEnd = (entry) => {
    if (entry.id === "rinse-0") {
      return (
        milestonesByStage[MILESTONE_STAGES.RINSE_END]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.MASH_START]?.[0]?.date ||
        (actuals[2] ? new Date(actuals[2]) : null)
      );
    }
    const mashMatch = entry.id.match(/^mash-(\d+)-(\d+)$/);
    if (mashMatch) {
      return (
        milestonesByStage[MILESTONE_STAGES.MASH_END]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.BOIL_HEATUP_START]?.[0]?.date ||
        (actuals[3] ? new Date(actuals[3]) : null)
      );
    }
    if (entry.id === "boil-0") {
      return (
        milestonesByStage[MILESTONE_STAGES.BOIL_END]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.COOLING_START]?.[0]?.date ||
        (actuals[4] ? new Date(actuals[4]) : null)
      );
    }
    if (entry.id === "boil-chill") {
      return (
        milestonesByStage[MILESTONE_STAGES.COOLING_END]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.PITCH_COOLING_START]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.FERMENTATION_START]?.[0]?.date ||
        (actuals[5] ? new Date(actuals[5]) : null)
      );
    }
    const fermMatch = entry.id.match(/^ferm-(\d+)-(\d+)$/);
    if (fermMatch) {
      return (
        milestonesByStage[MILESTONE_STAGES.CARBONATION_START]?.[0]?.date ||
        milestonesByStage[MILESTONE_STAGES.SERVING_START]?.[0]?.date ||
        (actuals[8] ? new Date(actuals[8]) : null)
      );
    }
    return null;
  };

  // 1. Determine currently active entry (if any)
  let activeEntry = null;

  if (isSessionActive && currentStatus) {
    const {
      procType,
      procState,
      currentStepIndex,
      currentTempStepIndex,
      setpointTemp,
    } = currentStatus;

    if (procType === PROCESS_TYPES.PROC_BREWING) {
      if (procState !== null && procState !== undefined) {
        if (procState < 20) {
          activeEntry = allEntries.find((e) => e.id === "rinse-0");
        } else if (procState >= 20 && procState < 50) {
          if (
            currentStepIndex !== undefined &&
            currentTempStepIndex !== undefined
          ) {
            activeEntry = allEntries.find(
              (e) =>
                e.id === `mash-${currentStepIndex}-${currentTempStepIndex}`,
            );
          }
          if (!activeEntry) {
            activeEntry = allEntries.find((e) => e.stage === "MASHING");
          }
        } else if (procState >= 50 && procState < 60) {
          activeEntry = allEntries.find((e) => e.id === "boil-0");
        } else if (procState >= 60 && procState < 74) {
          activeEntry = allEntries.find((e) => e.id === "boil-chill");
        }
      }
    } else if (procType === PROCESS_TYPES.PROC_FERMENTATION) {
      let targetFermStageIdx = null;
      if (setpointTemp !== undefined && setpointTemp !== null) {
        const matched = allEntries.find(
          (e) => e.stage === "FERMENTATION" && e.temperature === setpointTemp,
        );
        if (matched) {
          const m = matched.id.match(/^ferm-(\d+)-\d+$/);
          if (m) targetFermStageIdx = parseInt(m[1], 10);
        }
      }

      const fermIdx = getFermentationStageIndex(
        targetFermStageIdx,
        currentStepIndex,
        currentTempStepIndex,
        procState,
      );

      activeEntry =
        allEntries.find((e) => e.id === `ferm-${fermIdx}-0`) ||
        allEntries.find((e) => e.id.startsWith(`ferm-${fermIdx}-`)) ||
        allEntries.find((e) => e.stage === "FERMENTATION");
    } else if (procType === PROCESS_TYPES.PROC_SERVING) {
      activeEntry = allEntries.find((e) => e.id === "serving-0");
    }
  }

  // 2. Identify highest reached entry for inactive sessions
  let highestReachedOrder = -1;
  if (!isSessionActive) {
    if (actuals[8] || milestonesByStage[MILESTONE_STAGES.SERVING_START]) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "serving-0")?.stageOrder ?? -1;
    } else if (actuals[7]) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "ferm-2-0")?.stageOrder ?? -1;
    } else if (actuals[6]) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "ferm-1-0")?.stageOrder ?? -1;
    } else if (
      actuals[5] ||
      milestonesByStage[MILESTONE_STAGES.FERMENTATION_START] ||
      milestonesByStage[MILESTONE_STAGES.PITCH_COOLING_START]
    ) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "ferm-0-0")?.stageOrder ?? -1;
    } else if (
      actuals[4] ||
      milestonesByStage[MILESTONE_STAGES.COOLING_START]
    ) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "boil-chill")?.stageOrder ?? -1;
    } else if (
      actuals[3] ||
      milestonesByStage[MILESTONE_STAGES.BOIL_MAINTAIN_START] ||
      milestonesByStage[MILESTONE_STAGES.BOIL_HEATUP_START]
    ) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "boil-0")?.stageOrder ?? -1;
    } else if (actuals[2] || milestonesByStage[MILESTONE_STAGES.MASH_START]) {
      highestReachedOrder =
        allEntries.filter((e) => e.stage === "MASHING").pop()?.stageOrder ?? -1;
    } else if (actuals[1] || milestonesByStage[MILESTONE_STAGES.RINSE_START]) {
      highestReachedOrder =
        allEntries.find((e) => e.id === "rinse-0")?.stageOrder ?? -1;
    }

    if (session.furthest_status && session.furthest_status.procState) {
      const fsState = session.furthest_status.procState;
      if (fsState >= 101) {
        highestReachedOrder = Math.max(
          highestReachedOrder,
          allEntries.find((e) => e.id === "serving-0")?.stageOrder ?? -1,
        );
      } else if (fsState >= 88) {
        highestReachedOrder = Math.max(
          highestReachedOrder,
          allEntries.filter((e) => e.stage === "FERMENTATION").pop()
            ?.stageOrder ?? -1,
        );
      } else if (fsState >= 74) {
        highestReachedOrder = Math.max(
          highestReachedOrder,
          allEntries.find((e) => e.id === "boil-chill")?.stageOrder ?? -1,
        );
      } else if (fsState >= 50) {
        highestReachedOrder = Math.max(
          highestReachedOrder,
          allEntries.find((e) => e.id === "boil-0")?.stageOrder ?? -1,
        );
      }
    }
  }

  // -------------------------------------------------------------------------
  // PHASE 2: ZONE A - COMPLETED STEPS (FROZEN HISTORY)
  // -------------------------------------------------------------------------
  let prevStepEnd = sessionStart;

  allEntries.forEach((entry) => {
    let isCompleted = false;

    if (isSessionActive && activeEntry) {
      if (entry.stageOrder < activeEntry.stageOrder) {
        isCompleted = true;
      } else if (stageIsForceCompleted(entry.stage, currentStatus)) {
        isCompleted = true;
      }
    } else if (isSessionFinished) {
      if (entry.stageOrder <= highestReachedOrder && entry.id !== "serving-0") {
        isCompleted = true;
      }
    } else if (isSessionAborted) {
      if (
        entry.stageOrder < highestReachedOrder ||
        stageIsForceCompleted(entry.stage, currentStatus)
      ) {
        isCompleted = true;
      }
    }

    if (isCompleted) {
      entry.status = "completed";
      const mStart = getMilestoneStart(entry);
      const mEnd = getMilestoneEnd(entry);

      if (mStart) {
        entry.startTime = mStart;
      } else if (prevStepEnd) {
        entry.startTime = prevStepEnd;
      }

      if (mEnd) {
        entry.endTime = mEnd;
      } else {
        const durMin = entry.duration || 0;
        entry.endTime = new Date(
          entry.startTime.getTime() + durMin * 60 * 1000,
        );
      }
      prevStepEnd = entry.endTime;

      // Lock hop drops
      if (entry.stage === "BOILING" && entry.children) {
        const hopMilestones =
          milestonesByStage[MILESTONE_STAGES.BOIL_HOP_DROP] || [];
        const nominalBoilMin = entry.duration || 1;
        const actualBoilMs =
          entry.endTime && entry.endTime > entry.startTime
            ? entry.endTime.getTime() - entry.startTime.getTime()
            : nominalBoilMin * 60 * 1000;

        entry.children.forEach((child, idx) => {
          const m =
            hopMilestones.find(
              (h) => h.ingredient_id === child.ingredient_id,
            ) || hopMilestones[idx];
          if (m) {
            child.dropTime = m.date;
            child.isDropped = true;
            child.status = "completed";
          } else {
            // When actual boil duration is shorter than nominal (e.g. simulator accelerated time,
            // fast-forwarded or ended early), scale dropTime proportionally within actual boil window.
            let dropTime;
            if (actualBoilMs < nominalBoilMin * 60 * 1000) {
              const progressRatio = Math.min(
                1,
                Math.max(0, child.dropMinute / nominalBoilMin),
              );
              dropTime = new Date(
                entry.startTime.getTime() + actualBoilMs * progressRatio,
              );
            } else {
              dropTime = new Date(
                entry.startTime.getTime() + child.dropMinute * 60 * 1000,
              );
            }
            if (entry.endTime && dropTime > entry.endTime) {
              dropTime = entry.endTime;
            }
            child.dropTime = dropTime;
            child.isDropped = true;
            child.status = "completed";
          }
        });
      }
    }
  });

  // -------------------------------------------------------------------------
  // PHASE 3: ZONE B - ACTIVE STEP (ANCHORED ETA, ZERO FLUTTER)
  // -------------------------------------------------------------------------
  if (isSessionActive && activeEntry) {
    activeEntry.status = "active";
    const mStart = getMilestoneStart(activeEntry);

    if (mStart) {
      activeEntry.startTime = mStart;
    } else if (prevStepEnd) {
      activeEntry.startTime = prevStepEnd;
    }

    if (activeEntry.stage === "FERMENTATION") {
      const days =
        activeEntry.durationDays !== undefined
          ? activeEntry.durationDays
          : activeEntry.duration
            ? activeEntry.duration / (24 * 60)
            : 0;
      activeEntry.endTime = new Date(
        activeEntry.startTime.getTime() + days * 24 * 60 * 60 * 1000,
      );
    } else if (activeEntry.stage === "SERVING") {
      activeEntry.endTime = null;
      const actualServingStart = activeEntry.startTime;
      const diffMs = Date.now() - actualServingStart.getTime();
      const days = Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
      activeEntry.durationLabel = days === 1 ? "1 day" : `${days} days`;
    } else {
      const durMin = activeEntry.duration || 0;
      activeEntry.endTime = new Date(
        activeEntry.startTime.getTime() + durMin * 60 * 1000,
      );
    }
    prevStepEnd = activeEntry.endTime || activeEntry.startTime;

    // Hops in active boil
    if (activeEntry.stage === "BOILING" && activeEntry.children) {
      const hopMilestones =
        milestonesByStage[MILESTONE_STAGES.BOIL_HOP_DROP] || [];
      activeEntry.children.forEach((child, idx) => {
        const m =
          hopMilestones.find((h) => h.ingredient_id === child.ingredient_id) ||
          hopMilestones[idx];
        if (m) {
          child.dropTime = m.date;
          child.isDropped = true;
          child.status = "completed";
        } else {
          child.dropTime = new Date(
            activeEntry.startTime.getTime() + child.dropMinute * 60 * 1000,
          );
          child.isDropped = false;
          child.status = "upcoming";
        }
      });
    }
  }

  // -------------------------------------------------------------------------
  // PHASE 4: ZONE C - FUTURE STEPS (SINGLE START ETA ONLY)
  // -------------------------------------------------------------------------
  const futureEntries = allEntries.filter((e) => e.status === "upcoming");
  let nextProjectedStart = prevStepEnd;

  futureEntries.forEach((entry) => {
    if (isSessionFinished || isSessionAborted) {
      entry.startTime = null;
      entry.endTime = null;
    } else {
      const mStart = getMilestoneStart(entry);
      if (mStart) {
        entry.startTime = mStart;
        nextProjectedStart = mStart;
      } else {
        const entryIdx = allEntries.indexOf(entry);
        const prevEntry = allEntries[entryIdx - 1];
        let gapMs = 0;
        if (
          prevEntry &&
          prevEntry.stage === "MASHING" &&
          entry.stage === "BOILING" &&
          entry.id === "boil-0"
        ) {
          gapMs = POST_MASH_GAP * 60 * 1000;
        }
        entry.startTime = new Date(nextProjectedStart.getTime() + gapMs);
      }
      entry.endTime = null;

      if (entry.children) {
        entry.children.forEach((child) => {
          if (entry.stage === "BOILING") {
            child.dropTime = new Date(
              entry.startTime.getTime() + child.dropMinute * 60 * 1000,
            );
          } else {
            child.dropTime = entry.startTime;
          }
          child.isDropped = false;
          child.status = "upcoming";
        });
      }

      const durMin = entry.duration || 0;
      nextProjectedStart = new Date(
        entry.startTime.getTime() + durMin * 60 * 1000,
      );
    }
  });

  // Dynamic serving duration if already reached in inactive session
  const servingStage = stages.find((s) => s.stage === "SERVING");
  if (servingStage && servingStage.entries[0]) {
    const entry = servingStage.entries[0];
    const actualServingStart = actuals[8] ? new Date(actuals[8]) : null;
    if (actualServingStart) {
      entry.startTime = actualServingStart;
      entry.endTime = null;

      const isInactive =
        session.status === SESSION_STATES.STATUS_FINISHED ||
        session.status === SESSION_STATES.STATUS_ABORTED ||
        session.status === SESSION_STATES.STATUS_FAILED;

      const end =
        isInactive && session.modified
          ? new Date(session.modified)
          : new Date();

      const diffMs = end.getTime() - actualServingStart.getTime();
      const days = Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
      entry.durationLabel = days === 1 ? "1 day" : `${days} days`;
    }
  }

  return stages;
};

/**
 * Resolve phase status text for monitoring cards
 */
export const resolvePhaseStatus = (session, type, hasData, active) => {
  if (active) return "Active";
  if (!session) return "Not Started";

  const milestones = session.milestones || [];
  const actuals = session.actual_step_timestamps || {};

  const hasMilestone = (...stages) =>
    milestones.some((m) => stages.includes(m.stage));

  const hasActual = (...keys) => keys.some((k) => !!actuals[k]);

  // Finished sessions
  if (session.status === SESSION_STATES.STATUS_FINISHED) {
    if (type === PHASE_STATUS.BREWING) {
      return session.session_type === SESSION_TYPES.SESSION_TYPE_FERMENTATION
        ? "Not Started"
        : "Completed";
    }
    if (type === PHASE_STATUS.FERMENTATION) {
      return "Completed";
    }
    if (type === PHASE_STATUS.SERVING) {
      return hasMilestone(MILESTONE_STAGES.SERVING_START) || hasActual(8)
        ? "Completed"
        : "Not Started";
    }
    return "Completed";
  }

  // Active session where this phase is already completed or upcoming
  if (session.status === SESSION_STATES.STATUS_ACTIVE) {
    const procType = session.device?.process_type || 0;
    if (type === PHASE_STATUS.BREWING) {
      if (procType > PROCESS_TYPES.PROC_BREWING) return "Completed";
      if (procType === PROCESS_TYPES.PROC_BREWING) return "Active";
      return "Not Started";
    }
    if (type === PHASE_STATUS.FERMENTATION) {
      if (procType > PROCESS_TYPES.PROC_FERMENTATION) return "Completed";
      if (procType === PROCESS_TYPES.PROC_FERMENTATION) return "Active";
      return "Not Started";
    }
    if (type === PHASE_STATUS.SERVING) {
      return procType >= PROCESS_TYPES.PROC_SERVING ? "Active" : "Not Started";
    }
  }

  // Aborted or Failed sessions
  if (
    session.status === SESSION_STATES.STATUS_ABORTED ||
    session.status === SESSION_STATES.STATUS_FAILED
  ) {
    if (type === PHASE_STATUS.BREWING) {
      if (
        hasData ||
        hasMilestone(
          MILESTONE_STAGES.FERMENTATION_START,
          MILESTONE_STAGES.PITCH_COOLING_START,
          MILESTONE_STAGES.COOLING_END,
        ) ||
        hasActual(5, 4)
      ) {
        return "Completed";
      }
      if (
        hasMilestone(
          MILESTONE_STAGES.RINSE_START,
          MILESTONE_STAGES.MASH_START,
          MILESTONE_STAGES.BOIL_HEATUP_START,
        ) ||
        hasActual(1, 2, 3)
      ) {
        return "Aborted";
      }
      return "Not Started";
    }

    if (type === PHASE_STATUS.FERMENTATION) {
      if (
        hasMilestone(
          MILESTONE_STAGES.SERVING_START,
          MILESTONE_STAGES.CARBONATION_START,
        ) ||
        hasActual(8)
      ) {
        return "Completed";
      }
      if (
        hasMilestone(
          MILESTONE_STAGES.FERMENTATION_START,
          MILESTONE_STAGES.PITCH_COOLING_START,
        ) ||
        hasActual(5, 6, 7) ||
        hasData
      ) {
        return "Aborted";
      }
      return "Not Started";
    }

    if (type === PHASE_STATUS.SERVING) {
      if (
        hasMilestone(MILESTONE_STAGES.SERVING_START) ||
        hasActual(8) ||
        hasData
      ) {
        return "Aborted";
      }
      return "Not Started";
    }
  }

  return hasData ? "Completed" : "Not Started";
};

/**
 * Format a duration in milliseconds to human-readable string.
 * e.g. "11 min", "2h 15m", "4 days"
 */
export const formatDurationMs = (ms) => {
  if (!ms || ms <= 0 || isNaN(ms)) return null;
  const totalMinutes = Math.round(ms / (60 * 1000));
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  if (days < 7) {
    return remHours > 0
      ? `${days}d ${remHours}h`
      : `${days} ${days === 1 ? "day" : "days"}`;
  }
  return `${days} days`;
};

/**
 * Format a start and end time range for phase summary.
 * e.g. "Sep 22, 20:10 - 20:21" (same day)
 *      "Sep 22, 20:10 - Sep 26, 20:21" (cross day)
 *      "Started Sep 22, 20:10"
 */
export const formatPhaseTimeRange = (startTime, endTime) => {
  if (!startTime && !endTime) return null;
  if (startTime && !endTime) {
    const startM = moment(startTime);
    return startM.isValid() ? `Started ${startM.format("MMM D, HH:mm")}` : null;
  }
  if (!startTime && endTime) {
    const endM = moment(endTime);
    return endM.isValid() ? `Ended ${endM.format("MMM D, HH:mm")}` : null;
  }
  const startM = moment(startTime);
  const endM = moment(endTime);
  if (!startM.isValid() || !endM.isValid()) return null;

  if (startM.isSame(endM, "day")) {
    return `${startM.format("MMM D, HH:mm")} - ${endM.format("HH:mm")}`;
  }
  return `${startM.format("MMM D, HH:mm")} - ${endM.format("MMM D, HH:mm")}`;
};

/**
 * Extract time details (startTime, endTime, duration, status) for a phase.
 * @param {Object} session - WorkingSession object
 * @param {number} type - PHASE_STATUS (BREWING: 0, FERMENTATION: 1, SERVING: 2, CLEANING: 3)
 * @param {Date|number|string} [liveStart] - Optional live start from chart logs
 * @param {Date|number|string} [liveEnd] - Optional live end from chart logs
 * @returns {Object} { startTime, endTime, durationMs, durationFormatted, timeRangeFormatted, status }
 */
export const getPhaseTimeDetails = (session, type, liveStart, liveEnd) => {
  if (!session) {
    return {
      startTime: null,
      endTime: null,
      durationMs: null,
      durationFormatted: null,
      timeRangeFormatted: null,
      status: "Not Started",
    };
  }

  const milestones = session.milestones || [];
  const actuals = session.actual_step_timestamps || {};

  const findMilestoneDate = (...stageNames) => {
    for (const name of stageNames) {
      const m = milestones.find((x) => x.stage === name);
      if (m && m.recorded_at) {
        const d = new Date(m.recorded_at);
        if (!isNaN(d.getTime())) return d;
      }
    }
    return null;
  };

  const getActualDate = (...keys) => {
    for (const k of keys) {
      const val = actuals[k];
      if (val) {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d;
      }
    }
    return null;
  };

  const toDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  const chartStart = toDate(liveStart);
  const chartEnd = toDate(liveEnd);

  let startTime = null;
  let endTime = null;

  if (type === PHASE_STATUS.BREWING) {
    startTime =
      findMilestoneDate(
        MILESTONE_STAGES.RINSE_START,
        MILESTONE_STAGES.MASH_START,
        MILESTONE_STAGES.MASH_STEP_START,
        MILESTONE_STAGES.BOIL_HEATUP_START,
      ) ||
      getActualDate(1, 2, 3, 4) ||
      chartStart ||
      (session.brew_timestamp
        ? new Date(session.brew_timestamp * 1000)
        : null) ||
      toDate(session.started_at) ||
      toDate(session.created);

    const brewFinishedDate =
      findMilestoneDate(
        MILESTONE_STAGES.FERMENTATION_START,
        MILESTONE_STAGES.PITCH_COOLING_START,
        MILESTONE_STAGES.COOLING_END,
        MILESTONE_STAGES.BOIL_END,
      ) || getActualDate(5, 4);

    if (brewFinishedDate) {
      endTime = brewFinishedDate;
    } else if (
      chartEnd &&
      (session.status === SESSION_STATES.STATUS_FINISHED ||
        session.status === SESSION_STATES.STATUS_ABORTED ||
        session.status === SESSION_STATES.STATUS_FAILED)
    ) {
      endTime = chartEnd;
    } else if (
      session.status === SESSION_STATES.STATUS_ABORTED ||
      session.status === SESSION_STATES.STATUS_FAILED
    ) {
      endTime = toDate(session.modified);
    }
  } else if (type === PHASE_STATUS.FERMENTATION) {
    startTime =
      findMilestoneDate(
        MILESTONE_STAGES.FERMENTATION_START,
        MILESTONE_STAGES.PITCH_COOLING_START,
      ) ||
      getActualDate(5, 6, 7) ||
      chartStart;

    const fermFinishedDate =
      findMilestoneDate(
        MILESTONE_STAGES.SERVING_START,
        MILESTONE_STAGES.CARBONATION_START,
      ) || getActualDate(8);

    if (fermFinishedDate) {
      endTime = fermFinishedDate;
    } else if (
      chartEnd &&
      (session.status === SESSION_STATES.STATUS_FINISHED ||
        session.status === SESSION_STATES.STATUS_ABORTED ||
        session.status === SESSION_STATES.STATUS_FAILED)
    ) {
      endTime = chartEnd;
    } else if (
      session.status === SESSION_STATES.STATUS_ABORTED ||
      session.status === SESSION_STATES.STATUS_FAILED
    ) {
      if (startTime) {
        endTime = toDate(session.modified);
      }
    }
  } else if (type === PHASE_STATUS.SERVING) {
    startTime =
      findMilestoneDate(MILESTONE_STAGES.SERVING_START) ||
      getActualDate(8) ||
      chartStart;

    if (
      session.status === SESSION_STATES.STATUS_FINISHED ||
      session.status === SESSION_STATES.STATUS_ABORTED ||
      session.status === SESSION_STATES.STATUS_FAILED
    ) {
      endTime = chartEnd || toDate(session.modified);
    }
  } else if (type === PHASE_STATUS.CLEANING) {
    startTime = getActualDate(1) || chartStart || toDate(session.created);

    if (
      session.status === SESSION_STATES.STATUS_FINISHED ||
      session.status === SESSION_STATES.STATUS_ABORTED ||
      session.status === SESSION_STATES.STATUS_FAILED
    ) {
      endTime = chartEnd || toDate(session.modified);
    }
  }

  const hasData = !!(chartStart || chartEnd);
  const activeProc = session.device?.process_type || 0;
  const isCurrentActive =
    (type === PHASE_STATUS.BREWING &&
      activeProc === PROCESS_TYPES.PROC_BREWING) ||
    (type === PHASE_STATUS.FERMENTATION &&
      activeProc === PROCESS_TYPES.PROC_FERMENTATION) ||
    (type === PHASE_STATUS.SERVING &&
      activeProc === PROCESS_TYPES.PROC_SERVING) ||
    (type === PHASE_STATUS.CLEANING &&
      (activeProc === PROCESS_TYPES.PROC_CLEAN_MINIBREW ||
        activeProc === PROCESS_TYPES.PROC_CLEAN_KEG ||
        activeProc === PROCESS_TYPES.PROC_ACID_CLEAN_MINIBREW));

  const status = resolvePhaseStatus(session, type, hasData, isCurrentActive);

  if (status === "Active") {
    endTime = null;
  } else if (status === "Not Started") {
    startTime = null;
    endTime = null;
  }

  let durationMs = null;
  if (startTime && endTime) {
    durationMs = Math.max(0, endTime.getTime() - startTime.getTime());
  } else if (startTime && status === "Active") {
    durationMs = Math.max(0, Date.now() - startTime.getTime());
  }

  return {
    startTime,
    endTime,
    durationMs,
    durationFormatted: formatDurationMs(durationMs),
    timeRangeFormatted: formatPhaseTimeRange(startTime, endTime),
    status,
  };
};

/**
 * Format a Date object to a user-friendly string
 * @param {Date} date
 * @returns {string}
 */
export const formatTimelineDate = (date) => {
  if (!date) return "—";
  const options = {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  return date.toLocaleDateString(undefined, options);
};

/**
 * Format a Date object to just the time
 * @param {Date} date
 * @returns {string}
 */
export const formatTimelineTime = (date) => {
  if (!date) return "—";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};
